import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/apiService';
import './PartnerDashboardPage.css';
import Footer from '../components/Footer';
import Header from '../components/Header';

const PartnerDashboardPage = () => {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('upcoming');
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // OTP states
  const [startWorkOTP, setStartWorkOTP] = useState('');
  const [endWorkOTP, setEndWorkOTP] = useState('');
  const [isGeneratingEndOTP, setIsGeneratingEndOTP] = useState(false);
  const [isVerifyingStartOTP, setIsVerifyingStartOTP] = useState(false);
  const [isVerifyingEndOTP, setIsVerifyingEndOTP] = useState(false);

  const TAB_STATUS = { upcoming: 'not_started', active: 'started', completed: 'completed' };

  const parseAddress = (address) => {
    if (!address) return null;
    if (typeof address === 'string') { try { return JSON.parse(address); } catch { return null; } }
    return address;
  };

  const mapOrder = (order, items, customerPhone) => ({
    orderId: order.id,
    orderNumber: order.id,
    workStatus: order.work_status,
    orderStatus: order.status,
    scheduledDate: order.scheduled_date,
    scheduledTimeLabel: order.scheduled_time_label,
    customerPhone: customerPhone || order.user_id,
    initialAmount: order.total_amount,
    finalAmount: order.final_amount,
    actualHoursWorked: order.actual_hours_worked,
    workStartedAt: order.work_started_at,
    workCompletedAt: order.work_completed_at,
    startWorkOTP: order.start_work_otp,
    endWorkOTP: order.end_work_otp,
    address: parseAddress(order.address),
    services: items
      ? items.map(item => ({
          name: item.service_name,
          category: item.category,
          quantity: item.quantity,
          pricePerHour: item.price_per_hour,
          totalPrice: item.total_price,
        }))
      : [],
  });

  useEffect(() => {
    if (!partnerId) {
      setError('Partner ID is required');
      setIsLoading(false);
      return;
    }
    fetchOrders();
  }, [partnerId, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOrders = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.get('/orders', { work_status: TAB_STATUS[activeTab] });
      setOrders((data.data || []).map(o => mapOrder(o, null, '')));
    } catch (err) {
      setError('Failed to fetch orders. Please try again.');
      console.error('Error fetching orders:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOrderDetails = async (orderId) => {
    try {
      setError(null);
      const [orderRes, itemsRes] = await Promise.all([
        api.get(`/orders/${orderId}`),
        api.get('/order-items', { order_id: orderId }),
      ]);
      const order = orderRes.data;
      const items = itemsRes.data || [];
      let customerPhone = '';
      try {
        const userRes = await api.get(`/users/${order.user_id}`);
        customerPhone = userRes.data?.phone_number || '';
      } catch {}
      setSelectedOrder(mapOrder(order, items, customerPhone));
    } catch (err) {
      setError('Failed to fetch order details. Please try again.');
      console.error('Error fetching order details:', err);
    }
  };

  const handleOrderClick = (order) => {
    fetchOrderDetails(order.orderId);
  };

  const handleGenerateEndOTP = async () => {
    if (!selectedOrder) return;
    try {
      setIsGeneratingEndOTP(true);
      setError(null);
      setSuccess(null);
      await api.post(`/orders/${selectedOrder.orderId}/generate-end-otp`);
      setSuccess('OTP sent to customer. Please ask customer for the OTP.');
      await fetchOrderDetails(selectedOrder.orderId);
    } catch (err) {
      setError('Failed to generate end work OTP. Please try again.');
      console.error('Error generating end OTP:', err);
    } finally {
      setIsGeneratingEndOTP(false);
    }
  };

  const handleVerifyStartOTP = async () => {
    if (!selectedOrder || !startWorkOTP || startWorkOTP.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }
    try {
      setIsVerifyingStartOTP(true);
      setError(null);
      setSuccess(null);
      await api.post(`/orders/${selectedOrder.orderId}/verify-start-otp`, { otp: startWorkOTP });
      setSuccess('Work started successfully!');
      setStartWorkOTP('');
      await fetchOrderDetails(selectedOrder.orderId);
      await fetchOrders();
    } catch (err) {
      setError(err.message || 'Invalid or expired OTP');
      console.error('Error verifying start OTP:', err);
    } finally {
      setIsVerifyingStartOTP(false);
    }
  };

  const handleVerifyEndOTP = async () => {
    if (!selectedOrder || !endWorkOTP || endWorkOTP.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }
    try {
      setIsVerifyingEndOTP(true);
      setError(null);
      setSuccess(null);
      const data = await api.post(`/orders/${selectedOrder.orderId}/verify-end-otp`, { otp: endWorkOTP });
      setSuccess(`Work completed successfully! Final amount: ₹${data.finalAmount}`);
      setEndWorkOTP('');
      await fetchOrderDetails(selectedOrder.orderId);
      await fetchOrders();
    } catch (err) {
      setError(err.message || 'Invalid or expired OTP');
      console.error('Error verifying end OTP:', err);
    } finally {
      setIsVerifyingEndOTP(false);
    }
  };

  const getFilteredOrders = () => {
    if (!selectedOrder) return orders;
    
    switch (activeTab) {
      case 'upcoming':
        return orders.filter(o => o.workStatus === 'not_started');
      case 'active':
        return orders.filter(o => o.workStatus === 'started');
      case 'completed':
        return orders.filter(o => o.workStatus === 'completed');
      default:
        return orders;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const maskPhoneNumber = (phone) => {
    if (!phone) return 'N/A';
    return phone.replace(/(\d{2})\d{6}(\d{2})/, '$1******$2');
  };

  const getWorkStatusBadge = (status) => {
    const badges = {
      'not_started': { text: 'Ready to Start', class: 'badge-ready' },
      'started': { text: 'In Progress', class: 'badge-progress' },
      'completed': { text: 'Completed', class: 'badge-completed' }
    };
    const badge = badges[status] || { text: status, class: 'badge-default' };
    return <span className={`status-badge ${badge.class}`}>{badge.text}</span>;
  };

  const getOrderStatusBadge = (status) => {
    const badges = {
      'pending': { text: 'Pending', class: 'badge-pending' },
      'confirmed': { text: 'Confirmed', class: 'badge-confirmed' },
      'in_progress': { text: 'In Progress', class: 'badge-progress' },
      'completed': { text: 'Completed', class: 'badge-completed' },
      'cancelled': { text: 'Cancelled', class: 'badge-cancelled' }
    };
    const badge = badges[status] || { text: status, class: 'badge-default' };
    return <span className={`status-badge ${badge.class}`}>{badge.text}</span>;
  };

  return (
    <div className="partner-dashboard-page">
      <div className="partner-dashboard-container">
        <Header
          slogan="Partner Dashboard"
          rightContent={<button className="back-btn" onClick={() => navigate('/home')}>← Back to Home</button>}
        />

        {error && (
          <div className="alert alert-error">
            {error}
            <button className="alert-close" onClick={() => setError(null)}>×</button>
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            {success}
            <button className="alert-close" onClick={() => setSuccess(null)}>×</button>
          </div>
        )}

        <main className="dashboard-content">
          {!selectedOrder ? (
            <>
              <div className="tabs-container">
                <button
                  className={`tab ${activeTab === 'upcoming' ? 'active' : ''}`}
                  onClick={() => setActiveTab('upcoming')}
                >
                  Upcoming
                </button>
                <button
                  className={`tab ${activeTab === 'active' ? 'active' : ''}`}
                  onClick={() => setActiveTab('active')}
                >
                  Active
                </button>
                <button
                  className={`tab ${activeTab === 'completed' ? 'active' : ''}`}
                  onClick={() => setActiveTab('completed')}
                >
                  Completed
                </button>
              </div>

              {isLoading ? (
                <div className="loading-container">
                  <div className="spinner"></div>
                  <p>Loading orders...</p>
                </div>
              ) : getFilteredOrders().length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📋</div>
                  <h3>No orders found</h3>
                  <p>You don't have any {activeTab} orders at the moment.</p>
                </div>
              ) : (
                <div className="orders-grid">
                  {getFilteredOrders().map((order) => (
                    <div
                      key={order.orderId}
                      className="order-card"
                      onClick={() => handleOrderClick(order)}
                    >
                      <div className="order-card-header">
                        <h3>Order #{order.orderNumber || order.orderId.slice(-8)}</h3>
                        <div className="order-badges">
                          {getWorkStatusBadge(order.workStatus)}
                          {getOrderStatusBadge(order.orderStatus)}
                        </div>
                      </div>
                      <div className="order-card-body">
                        <div className="order-info-item">
                          <span className="info-label">📅 Date:</span>
                          <span className="info-value">{formatDate(order.scheduledDate)}</span>
                        </div>
                        <div className="order-info-item">
                          <span className="info-label">🕐 Time:</span>
                          <span className="info-value">{order.scheduledTimeLabel}</span>
                        </div>
                        <div className="order-info-item">
                          <span className="info-label">👤 Customer:</span>
                          <span className="info-value">{maskPhoneNumber(order.customerPhone)}</span>
                        </div>
                        <div className="order-info-item">
                          <span className="info-label">💰 Amount:</span>
                          <span className="info-value">₹{order.initialAmount}</span>
                        </div>
                        {order.workStatus === 'started' && order.workStartedAt && (
                          <div className="order-info-item">
                            <span className="info-label">⏱️ Started:</span>
                            <span className="info-value">
                              {new Date(order.workStartedAt).toLocaleTimeString()}
                            </span>
                          </div>
                        )}
                        {order.workStatus === 'completed' && order.workCompletedAt && (
                          <div className="order-info-item">
                            <span className="info-label">✅ Completed:</span>
                            <span className="info-value">
                              {new Date(order.workCompletedAt).toLocaleTimeString()}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="order-card-footer">
                        <button className="view-details-btn">View Details →</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="order-details-view">
              <button className="back-to-list-btn" onClick={() => setSelectedOrder(null)}>
                ← Back to Orders
              </button>

              <div className="order-details-card">
                <div className="order-details-header">
                  <h2>Order #{selectedOrder.orderNumber || selectedOrder.orderId.slice(-8)}</h2>
                  <div className="order-badges">
                    {getWorkStatusBadge(selectedOrder.workStatus)}
                    {getOrderStatusBadge(selectedOrder.orderStatus)}
                  </div>
                </div>

                <div className="details-section">
                  <h3>📋 Booking Information</h3>
                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="detail-label">Scheduled Date:</span>
                      <span className="detail-value">{formatDate(selectedOrder.scheduledDate)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Scheduled Time:</span>
                      <span className="detail-value">{selectedOrder.scheduledTimeLabel}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Customer Phone:</span>
                      <span className="detail-value">{maskPhoneNumber(selectedOrder.customerPhone)}</span>
                    </div>
                    {selectedOrder.customerName && (
                      <div className="detail-item">
                        <span className="detail-label">Customer Name:</span>
                        <span className="detail-value">{selectedOrder.customerName}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="details-section">
                  <h3>📍 Service Address</h3>
                  <div className="address-display">
                    {selectedOrder.address && (
                      <>
                        {selectedOrder.address.streetAddress && (
                          <div className="address-line">{selectedOrder.address.streetAddress}</div>
                        )}
                        {(selectedOrder.address.floor || selectedOrder.address.flatNumber) && (
                          <div className="address-line">
                            {selectedOrder.address.floor && `${selectedOrder.address.floor}, `}
                            {selectedOrder.address.flatNumber}
                          </div>
                        )}
                        {selectedOrder.address.landmark && (
                          <div className="address-line" style={{ fontStyle: 'italic', color: '#666' }}>
                            Landmark: {selectedOrder.address.landmark}
                          </div>
                        )}
                        <div className="address-line">
                          {selectedOrder.address.apartment}, {selectedOrder.address.city}
                        </div>
                        {selectedOrder.address.pin && (
                          <div className="address-line">PIN: {selectedOrder.address.pin}</div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className="details-section">
                  <h3>🛠️ Services to Provide</h3>
                  <div className="services-list">
                    {selectedOrder.services && selectedOrder.services.length > 0 ? (
                      selectedOrder.services.map((service, index) => (
                        <div key={index} className="service-item">
                          <div className="service-header">
                            <span className="service-name">{service.name}</span>
                            <span className="service-category">{service.category}</span>
                          </div>
                          <div className="service-details">
                            <span>Hours: {service.quantity}</span>
                            <span>Price/Hour: ₹{service.pricePerHour}</span>
                            <span className="service-total">Total: ₹{service.totalPrice}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="no-services">No services found</p>
                    )}
                  </div>
                </div>

                <div className="details-section">
                  <h3>💼 Work Actions</h3>
                  {selectedOrder.workStatus === 'not_started' && (
                    <div className="work-action-card">
                      <h4>Start Work</h4>
                      <p className="action-description">
                        Enter the OTP that was sent to the customer to start work.
                      </p>
                      {selectedOrder.startWorkOTP && (
                        <div className="otp-info">
                          <p>OTP sent to customer (expires in 10 minutes)</p>
                          {process.env.NODE_ENV === 'development' && (
                            <p className="dev-otp">Dev Mode OTP: {selectedOrder.startWorkOTP}</p>
                          )}
                        </div>
                      )}
                      <div className="otp-input-group">
                        <input
                          type="text"
                          className="otp-input"
                          placeholder="Enter 6-digit OTP"
                          value={startWorkOTP}
                          onChange={(e) => setStartWorkOTP(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          maxLength="6"
                        />
                        <button
                          className="action-btn primary"
                          onClick={handleVerifyStartOTP}
                          disabled={isVerifyingStartOTP || startWorkOTP.length !== 6}
                        >
                          {isVerifyingStartOTP ? 'Verifying...' : 'Start Work'}
                        </button>
                      </div>
                    </div>
                  )}

                  {selectedOrder.workStatus === 'started' && (
                    <div className="work-action-card">
                      <h4>Work In Progress</h4>
                      <p className="action-description">
                        Work started at: {selectedOrder.workStartedAt ? new Date(selectedOrder.workStartedAt).toLocaleString() : 'N/A'}
                      </p>
                      <button
                        className="action-btn secondary"
                        onClick={handleGenerateEndOTP}
                        disabled={isGeneratingEndOTP || !!selectedOrder.endWorkOTP}
                      >
                        {isGeneratingEndOTP ? 'Generating...' : selectedOrder.endWorkOTP ? 'End OTP Generated' : 'Finish Work'}
                      </button>
                      {selectedOrder.endWorkOTP && (
                        <div className="otp-input-group" style={{ marginTop: '15px' }}>
                          <p className="action-description">
                            OTP has been sent to customer. Please ask customer for the OTP to complete work.
                          </p>
                          {process.env.NODE_ENV === 'development' && (
                            <p className="dev-otp">Dev Mode OTP: {selectedOrder.endWorkOTP}</p>
                          )}
                          <input
                            type="text"
                            className="otp-input"
                            placeholder="Enter 6-digit OTP from customer"
                            value={endWorkOTP}
                            onChange={(e) => setEndWorkOTP(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            maxLength="6"
                          />
                          <button
                            className="action-btn primary"
                            onClick={handleVerifyEndOTP}
                            disabled={isVerifyingEndOTP || endWorkOTP.length !== 6}
                          >
                            {isVerifyingEndOTP ? 'Verifying...' : 'Complete Work'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedOrder.workStatus === 'completed' && (
                    <div className="work-action-card completed">
                      <h4>✅ Work Completed</h4>
                      <p className="action-description">
                        Work completed at: {selectedOrder.workCompletedAt ? new Date(selectedOrder.workCompletedAt).toLocaleString() : 'N/A'}
                      </p>
                      {selectedOrder.actualHoursWorked && (
                        <p className="action-description">
                          Actual hours worked: {selectedOrder.actualHoursWorked} hours
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="details-section">
                  <h3>💰 Pricing Information</h3>
                  <div className="pricing-info">
                    <div className="pricing-item">
                      <span className="pricing-label">Initial Amount (Scheduled):</span>
                      <span className="pricing-value">₹{selectedOrder.initialAmount}</span>
                    </div>
                    {selectedOrder.finalAmount && (
                      <>
                        <div className="pricing-item">
                          <span className="pricing-label">Final Amount (Actual):</span>
                          <span className="pricing-value final">₹{selectedOrder.finalAmount}</span>
                        </div>
                        {selectedOrder.actualHoursWorked && (
                          <div className="pricing-item">
                            <span className="pricing-label">Actual Hours Worked:</span>
                            <span className="pricing-value">{selectedOrder.actualHoursWorked} hours</span>
                          </div>
                        )}
                        {selectedOrder.finalAmount !== selectedOrder.initialAmount && (
                          <div className="pricing-item difference">
                            <span className="pricing-label">Difference:</span>
                            <span className="pricing-value">
                              ₹{Math.abs(selectedOrder.finalAmount - selectedOrder.initialAmount)}
                              {selectedOrder.finalAmount > selectedOrder.initialAmount ? ' (Additional)' : ' (Refund)'}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default PartnerDashboardPage;
